import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Row, Col, Card, CardBody, Input, Label } from 'reactstrap';
import { Btn, LI, UL } from 'AbstractElements';
import { XCircle, CheckSquare } from 'react-feather';
import GenerateUniqueContain from 'Component/Learning/Common/GenerateUnique';
import RawHTMLComponent from 'Component/Learning/Common/RawHTML';
import VideoPlayer from 'Component/Learning/DetailSubMateri/VideoPlayer';
import AudioPlayer from 'Component/Learning/DetailSubMateri/AudioPlayer';
import Moment from 'moment';
import 'moment/locale/id';
import formatNumberCompact from 'Utils/formatNumberCompact';
import { getInteractiveType, getNonInteractiveQuestionType } from 'Utils/getQuestionType';
import ModalComponent from 'Component/Learning/Common/Modal/ModalGeneral';
import AdminShapePreview from 'Component/Learning/BankQuestion/AdminShapePreview';
// import AdminDragPinPreview from 'Component/Learning/BankQuestion/AdminDragPinPreview';
import InteractiveDragPinPreview from 'Component/Learning/BankQuestion/InteractiveDragPinPreview';
import AdminSequentialPreview from 'Component/Learning/BankQuestion/AdminSequentialPreview';
import AdminLinePreview from 'Component/Learning/BankQuestion/AdminLinePreview';
import AdminBarPreview from 'Component/Learning/BankQuestion/AdminBarPreview';
import AdminPiePreview from 'Component/Learning/BankQuestion/AdminPiePreview';
import {
  CLICKABLE_LENGTH_BY_TYPE,
  getShapePreset,
  getShapeVariant,
} from 'Component/Learning/BankQuestion/questionsForm.helpers';
import RawComponent from 'Component/Learning/Common/RawHTML';

const QuestionsDetails = () => {
  const detail = useSelector(state => state.Question.detail);
  // NOTE: JSON dari backend kadang tidak selalu valid / bisa null.
  // Guard ini penting supaya halaman detail tidak crash, termasuk untuk mode clickable.
  const safeJsonParseArray = (value) => {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  //// Memeriksa apakah item.answer[0].answer adalah string JSON yang valid
  const answerArray = detail.answer && detail.answer[0] && detail.answer[0].answer
    ? safeJsonParseArray(detail.answer[0].answer)
    : [];

  // ✅ Normalize flags: di project ini kadang number 1/0 kadang string '1'/'0'
  const isInteractiveEnabled =
    detail?.is_interactive === 1 ||
    detail?.is_interactive === '1' ||
    detail?.is_interactive === true;
  const isClickableInteractive = isInteractiveEnabled && detail?.interactive_type === 'clickable';
  const isDragpinInteractive = isInteractiveEnabled && detail?.interactive_type === 'dragpin';
  const isSequentialInteractive = isInteractiveEnabled && detail?.interactive_type === 'sequential';
  const isLineChartInteractive = isInteractiveEnabled && detail?.interactive_type === 'line_chart';
  const isBarChartInteractive = isInteractiveEnabled && detail?.interactive_type === 'bar_chart';
  const isPieChartInteractive = isInteractiveEnabled && detail?.interactive_type === 'pie_chart';

  // clickable_type bisa ada di root atau nested
  const clickableType = detail?.clickable_type ?? detail?.interactive_detail?.[0]?.clickable_type;
  const clickableTypeLabel = clickableType === 'triangle'
    ? 'Segitiga'
    : clickableType === 'box'
      ? 'Persegi'
      : clickableType === 'boxhard'
        ? 'Persegi Kompleks'
        : 'Other';

  // jumlah cell untuk clickable mengikuti mapping existing
  const clickableCellCount = clickableType ? CLICKABLE_LENGTH_BY_TYPE[clickableType] : undefined;
  const clickablePreset = isClickableInteractive ? getShapePreset(answerArray) : [];

  // dragpin detail biasanya tersimpan di interactive_detail[0]
  // NOTE: fallback ke root untuk berjaga-jaga jika backend mengirim field di root.
  const dragpinRoomCount = detail?.room_count ?? detail?.interactive_detail?.[0]?.room_count;
  const dragpinPinCount = detail?.pin_count ?? detail?.interactive_detail?.[0]?.pin_count;
  const dragpinPinsMeta = Array.isArray(detail?.interactive_detail?.[0]?.dragpin_pins)
    ? detail.interactive_detail[0].dragpin_pins
    : [];

  // Membuat array baru untuk menyimpan hasil pengecekan
  const results = detail.type && detail.type === "multiple_choice" && detail.choices && detail.choices.map(choice => {
    // Memeriksa apakah id choice ada dalam answerArray
    if (answerArray.includes(String(choice.id))) {
      return <><CheckSquare key={choice.id} color="green" style={{ verticalAlign: 'middle' }} /><span style={{ verticalAlign: 'middle' }}>  Benar</span></>
    }
    return <><XCircle key={choice.id} color="red" style={{ verticalAlign: 'middle' }} /><span style={{ verticalAlign: 'middle' }}>  Salah</span></>
  });

  // Helper untuk replace "Room X" dengan custom room name jika ada
  const formatDragpinAnswer = (answer) => {
    const roomCustomNames = detail?.interactive_detail?.[0]?.room_custom_names;

    // Format dragpin: "Room 1:null" atau "Room 1:Nama1,Nama2"
    const match = answer.match(/^\s*Room\s*(\d+)\s*:\s*(.*)\s*$/i);
    if (!match) return answer;

    const roomNumber = Number(match[1]);
    const pinNames = (match[2] ?? '').trim();

    // Jika ada custom room names, gunakan custom name; jika tidak, pakai "Room X"
    let roomLabel = `Room ${roomNumber}`;
    if (Array.isArray(roomCustomNames) && roomCustomNames[roomNumber - 1]) {
      const customName = String(roomCustomNames[roomNumber - 1]).trim();
      if (customName) roomLabel = customName;
    }

    return `${roomLabel}:${pinNames}`;
  };

  return (
    <Col xl="5" className="box-col-12 proorder-xl-3 xl-100">
      <Card>
        <CardBody>
          <div className="pro-group pt-0 border-0">
            <Row>
              <Col xs="auto">
                <div className="product-price">{'Soal ID : '}{GenerateUniqueContain(detail.type === 'multiple_choice' ? 'MC' : detail.type === 'fill_in_the_blanks' ? 'FB' : detail.type === 'sentence_arrangement' ? 'SA' : 'undefined', detail.id, 5)}</div>
              </Col>
              <Col xs="auto">
                <div className="product-price">{'Tipe Soal : '}{getNonInteractiveQuestionType(detail)}</div>
              </Col>
              {detail.type === 'sentence_arrangement' && (
                <Col xs="auto">
                  <div className="product-price">{'Apakah soal interaktif : '}{isInteractiveEnabled ? 'Yes' : 'No'}</div>
                </Col>
              )}
              {isInteractiveEnabled && (
                <Col xs="auto">
                  <div className="product-price">{'Tipe Interaktif : '}{getInteractiveType(detail)}</div>
                </Col>
              )}
              {isClickableInteractive && (
                <Col xs="auto">
                  <div className="product-price">
                    {'Tipe Clickable : '}{clickableTypeLabel}
                  </div>
                </Col>
              )}
              {isClickableInteractive && (
                <Col xs="auto">
                  <div className="product-price">
                    {'Jumlah Cell : '}{clickableCellCount ? formatNumberCompact(clickableCellCount) : 'Tidak ada'}
                  </div>
                </Col>
              )}
              {(isInteractiveEnabled && (detail.interactive_type === 'bar_chart' || detail.interactive_type === 'line_chart')) && (
                <Col xs="auto">
                  <div className="product-price">
                    {'Step : '}{formatNumberCompact(detail?.interactive_detail?.[0]?.step || "1")}
                  </div>
                </Col>
              )}
              {isDragpinInteractive && (
                <Col xs="auto">
                  <div className="product-price">
                    {'Jumlah Room : '}{dragpinRoomCount ? formatNumberCompact(dragpinRoomCount) : 'Tidak ada'}
                  </div>
                </Col>
              )}
              {isDragpinInteractive && (
                <Col xs="auto">
                  <div className="product-price">
                    {'Jumlah Pin : '}{dragpinPinCount ? formatNumberCompact(dragpinPinCount) : 'Tidak ada'}
                  </div>
                </Col>
              )}
              {isClickableInteractive && clickableType && (
                <Col xs="auto">
                  <div className="product-price">
                    {/* Preview clickable agar admin bisa verifikasi pola yang tersimpan */}
                    <ModalComponent
                      title={`Preview Pola Bentuk (${clickableTypeLabel})`}
                      type="component"
                      content={
                        <AdminShapePreview
                          variant={getShapeVariant(clickableType)}
                          preset={clickablePreset}
                          readOnly={true}
                          height={420}
                        />
                      }
                      buttonLabel={<i className="fa fa-eye fa-lg"></i>}
                      buttonSize="sm"
                      size="lg"
                      messageTooltip="Preview pola clickable"
                    />
                  </div>
                </Col>
              )}
              {isDragpinInteractive && dragpinRoomCount && dragpinPinCount && (
                <Col xs="auto">
                  <div className="product-price">
                    {/* Preview dragpin interaktif — admin bisa drag pin seperti user */}
                    <ModalComponent
                      title="Preview Penempatan Drag Pin"
                      type="component"
                      content={
                        <InteractiveDragPinPreview
                          presetRoomCount={dragpinRoomCount}
                          presetPinCount={dragpinPinCount}
                          presetPinCustomNames={detail?.interactive_detail?.[0]?.pin_custom_names}
                          presetRoomCustomNames={detail?.interactive_detail?.[0]?.room_custom_names}
                          presetExplanation={{
                            userAnswerText: answerArray || [],
                            keyAnswerText: answerArray || []
                          }}
                          keyAnswerText={answerArray || []}
                          dragpinPins={detail?.interactive_detail?.[0]?.dragpin_pins || []}
                        />
                      }
                      buttonLabel={<i className="fa fa-eye fa-lg"></i>}
                      buttonSize="sm"
                      size="lg"
                      messageTooltip="Preview penempatan drag pin"
                    />
                  </div>
                </Col>
              )}
              {isSequentialInteractive && detail?.choices && detail.choices.length > 0 && (
                <Col xs="auto">
                  <div className="product-price">
                    {/* Preview sequential agar admin bisa verifikasi urutan yang tersimpan */}
                    <ModalComponent
                      title="Preview Urutan Jawaban"
                      type="component"
                      content={
                        <AdminSequentialPreview
                          items={detail.choices.map(c => c.choice)}
                          isExplanation={true}
                          presetOrder={answerArray || []}
                        />
                      }
                      buttonLabel={<i className="fa fa-eye fa-lg"></i>}
                      buttonSize="sm"
                      size="lg"
                      messageTooltip="Preview urutan sequential"
                    />
                  </div>
                </Col>
              )}
              {isLineChartInteractive && detail?.choices && detail.choices.length > 0 && (
                <Col xs="auto">
                  <div className="product-price">
                    {/* Preview line chart agar admin bisa verifikasi nilai yang tersimpan */}
                    <ModalComponent
                      title="Preview Line Chart"
                      type="component"
                      content={
                        <AdminLinePreview
                          items={detail.choices.map(c => c.choice)}
                          isExplanation={true}
                          presetValues={answerArray || []}
                          minMax={detail?.interactive_detail?.[0]?.minMax}
                          step={detail?.interactive_detail?.[0]?.step}
                        />
                      }
                      buttonLabel={<i className="fa fa-eye fa-lg"></i>}
                      buttonSize="sm"
                      size="lg"
                      messageTooltip="Preview line chart"
                    />
                  </div>
                </Col>
              )}
              {isBarChartInteractive && detail?.choices && detail.choices.length > 0 && (
                <Col xs="auto">
                  <div className="product-price">
                    {/* Preview bar chart agar admin bisa verifikasi nilai yang tersimpan */}
                    <ModalComponent
                      title="Preview Bar Chart"
                      type="component"
                      content={
                        <AdminBarPreview
                          items={detail.choices.map(c => c.choice)}
                          isExplanation={true}
                          presetValues={answerArray || []}
                          minMax={detail?.interactive_detail?.[0]?.minMax}
                          step={detail?.interactive_detail?.[0]?.step}
                        />
                      }
                      buttonLabel={<i className="fa fa-eye fa-lg"></i>}
                      buttonSize="sm"
                      size="lg"
                      messageTooltip="Preview bar chart"
                    />
                  </div>
                </Col>
              )}
              {isPieChartInteractive && detail?.choices && detail.choices.length > 0 && (
                <Col xs="auto">
                  <div className="product-price">
                    {/* Preview pie chart agar admin bisa verifikasi proporsi yang tersimpan */}
                    <ModalComponent
                      title="Preview Pie Chart"
                      type="component"
                      content={
                        <AdminPiePreview
                          items={detail.choices.map(c => c.choice)}
                          isExplanation={true}
                          presetPercentages={answerArray || []}
                          minProportion={detail?.interactive_detail?.[0]?.min_proportion}
                        />
                      }
                      buttonLabel={<i className="fa fa-eye fa-lg"></i>}
                      buttonSize="sm"
                      size="lg"
                      messageTooltip="Preview pie chart"
                    />
                  </div>
                </Col>
              )}
            </Row>
          </div>
          <div className="pro-group">
            <div className="product-price mb-3">{'Label Soal : '}
            </div>
            {detail.title}
          </div>
          <div className="pro-group">
            <div className="product-price mb-3">
              Audio Soal :
            </div>
            <p>
              {detail.audio_source === '' ? 'Tidak ada' : ''}
            </p>
            {detail.audio_source && (
              <AudioPlayer link={detail.audio_source} />
            )}
          </div>
          <div className="pro-group">
            <div className="product-price mb-3">{'Pertanyaan : '}
            </div>
            <RawHTMLComponent htmlContent={detail.description} />
          </div>
          {(detail.type === 'multiple_choice' || detail.type === 'sentence_arrangement') && (
            <div className="pro-group">
              <div className="product-price mb-3">{'Pilihan Jawaban : '}
              </div>
              <UL attrUL={{ className: 'simple-list' }}>
                {detail.choices && detail.choices.map((itemChoice, index) => (
                  <LI key={itemChoice.id} className="mt-3">
                    <div>
                      <Row>
                        <Col>
                          <Label htmlFor={`itemChoice-${index}`}><RawHTMLComponent htmlContent={itemChoice.choice} /></Label>
                        </Col>
                        <Col>
                          {results[index]}
                        </Col>
                      </Row>
                    </div>
                  </LI>
                ))}
              </UL>
            </div>
          )}
          <div className="pro-group">
            <div className="product-price mb-3">
              {'Pembahasan Soal : '}
            </div>
            <RawHTMLComponent htmlContent={detail.answer && detail.answer[0].answer_explanation} />
          </div>
          {detail.answer && (
            <div className="pro-group">
              <div className="product-price mb-3">
                Video Pembahasan Soal :
              </div>
              <p>
                {detail.answer[0].answer_explanation_video === null ? 'Tidak ada' : ''}
              </p>
              {detail.answer[0].answer_explanation_video && (
                <VideoPlayer link={detail.answer && detail.answer[0].answer_explanation_video} width={720} />
              )}
            </div>
          )}
          {(detail.type === 'fill_in_the_blanks' || detail.type === 'sentence_arrangement') && (
            <div className="pro-group">
              <div className="product-price mb-3">{'Jawaban Benar : '}
              </div>
              {isClickableInteractive && (
                <div className="mb-2">
                  {/* clickable value convention: 0=putih, 1=oranye, 2=pattern */}
                  <small>
                    Keterangan nilai: <b>0</b>=Putih, <b>1</b>=Oranye, <b>2</b>=Pola
                  </small>
                </div>
              )}
              {isDragpinInteractive && (
                <div className="mb-2">
                  {/* dragpin answer format: Room X:null atau Room X:Nama1,Nama2 */}
                  <small>
                    Keterangan dragpin: format jawaban <b>Room X:null</b> atau <b>Room X:Nama1,Nama2</b>
                  </small>
                </div>
              )}
              <UL attrUL={{ className: 'simple-list' }}>
                {answerArray && answerArray.map((itemAnswer, index) => (
                  <LI key={index} className="mt-3">
                    <div className="form-check">
                      <Input className="form-check-input" type="radio" value="" disabled />
                      <Label>
                        {detail?.interactive_type === 'bar_chart' || detail?.interactive_type === 'line_chart'
                          ? formatNumberCompact(itemAnswer)
                          : isDragpinInteractive
                            ? formatDragpinAnswer(itemAnswer)
                            : itemAnswer}
                      </Label>
                    </div>
                  </LI>
                ))}
              </UL>
            </div>
          )}
          {isDragpinInteractive && dragpinPinsMeta.length > 0 && (
            <div className="pro-group">
              <div className="product-price mb-3">{'Deskripsi Pin : '}</div>
              <UL attrUL={{ className: 'simple-list' }}>
                {dragpinPinsMeta.map((pin, idx) => (
                  <LI key={idx} className="mt-2">
                    <b>{pin.name}</b>: <RawComponent htmlContent={pin.label || '(Tidak ada deskripsi)'} />
                  </LI>
                ))}
              </UL>
            </div>
          )}
          <div className="pro-group pb-0">
            <Row className="product-page-main p-0">
              <Col>
                <div className="text-start">
                  <b>Created At : </b>{Moment(detail.created_at).format('dddd, DD/MM/yyyy')} - {Moment(detail.created_at).format('HH:mm:ss')} WIB
                </div>
                <div className="text-start">
                  <b>Updated At : </b>{Moment(detail.updated_at).format('dddd, DD/MM/yyyy')} - {Moment(detail.updated_at).format('HH:mm:ss')} WIB
                </div>
              </Col>
              <Col>
                <div className="pro-shop text-end">
                  <Link to={`${process.env.PUBLIC_URL}/learning/soal-list`}>
                    <Btn attrBtn={{ color: 'primary', className: 'btn btn-primary m-r-10' }}>
                      <i className="fa fa-chevron-circle-left fa-lg me-2"></i>Back
                    </Btn>
                  </Link>
                </div>
              </Col>
            </Row>
          </div>
        </CardBody>
      </Card>
    </Col>
  );
};
export default QuestionsDetails;